import clsx from 'clsx';
import type { FunctionComponent } from 'react';

import { Button } from '../button/Button';
import { IconHorizontalRule, IconPlus } from '../icons';
import { stepTableZoom, TABLE_ZOOM_DEFAULT, TABLE_ZOOM_MAX, TABLE_ZOOM_MIN, useTableZoom } from './TableZoomContext';

// Floating pill in the container corner. Hidden until the table is hovered or the control has focus.
export const TableZoomControl: FunctionComponent = () => {
    const { enabled, labels, setZoom, zoom } = useTableZoom();

    if (!enabled) return null;

    const percentage = Math.round(zoom * 100);

    return (
        // biome-ignore lint/a11y/useSemanticElements: a fieldset would add a border and legend box to a floating toolbar.
        <div
            aria-label={labels.group}
            className={clsx(
                'absolute bottom-m right-m z-[2] flex items-center gap-xxs rounded-lg border border-border-soft bg-surface-raised p-xxs shadow-sm',
                'opacity-0 transition-opacity duration-150 group-hover/table-container:opacity-100 focus-within:opacity-100',
            )}
            data-testid="table-zoom-control"
            role="group"
        >
            <Button
                aria-label={labels.zoomOut}
                disabled={zoom <= TABLE_ZOOM_MIN}
                iconOnly={true}
                size="sm"
                variant="tertiary"
                onClick={() => setZoom(stepTableZoom(zoom, -1))}
            >
                <IconHorizontalRule size={14} />
            </Button>
            <Button
                aria-label={labels.reset}
                className="min-w-[3.25rem] tabular-nums !text-text-muted"
                data-testid="table-zoom-value"
                disabled={zoom === TABLE_ZOOM_DEFAULT}
                size="sm"
                variant="tertiary"
                onClick={() => setZoom(TABLE_ZOOM_DEFAULT)}
            >
                {percentage}%
            </Button>
            <Button
                aria-label={labels.zoomIn}
                disabled={zoom >= TABLE_ZOOM_MAX}
                iconOnly={true}
                size="sm"
                variant="tertiary"
                onClick={() => setZoom(stepTableZoom(zoom, 1))}
            >
                <IconPlus size={14} />
            </Button>
        </div>
    );
};
